import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Chip,
  Typography,
  Tabs,
  Tab,
  Alert,
} from "@mui/material";
import AssessmentIcon from "@mui/icons-material/Assessment";

// Admin-only viewer for the persisted input-checker report.
// The report payload is whatever the backend stored in
// upload_file_validation_record.validation — i.e. the full dict returned by
// inputfile_checker_phase1_v1.run_all_checks(), including the legacy
// per-check lists and the v2 `findings` array.
//
// We only render this dialog when the caller is Admin (gated upstream in
// ReportInputTable). The button doesn't even appear for non-Admins, and the
// backend additionally refuses to ship `validation_report` to non-Admin
// callers — defense in depth.
const SEVERITY_COLOR = { critical: "error", warning: "warning", info: "info" };
const SEVERITY_ICON = { critical: "🔴", warning: "🟠", info: "ℹ️" };

export default function InputValidationReportDialog({ open, onClose, record }) {
  const [tab, setTab] = useState(0);

  if (!record) return null;

  const status = record.validation_status || "—";
  const summary = record.validation_summary || "(no summary)";
  const report = record.validation_report || {};
  const findings = record.findings || report.findings || [];

  const counts = findings.reduce(
    (acc, f) => {
      const s = (f.severity || "warning").toLowerCase();
      acc[s] = (acc[s] || 0) + 1;
      return acc;
    },
    { critical: 0, warning: 0, info: 0 }
  );

  // Group findings by severity for the "Findings" tab
  const grouped = ["critical", "warning", "info"].map((sev) => ({
    severity: sev,
    items: findings.filter((f) => (f.severity || "warning") === sev),
  }));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <AssessmentIcon fontSize="small" />
          Input checker report — {record.id || "?"}
          <Chip
            size="small"
            label={status}
            color={
              status === "error" ? "error" : status === "warn" ? "warning" : "success"
            }
          />
        </Box>
        <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 0.5 }}>
          Admin view. Users see only the critical problems, which they must fix before the file can be processed.
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ mb: 1, display: "flex", gap: 1 }}>
          <Chip size="small" color="error"   label={`🔴 ${counts.critical} critical`} />
          <Chip size="small" color="warning" label={`🟠 ${counts.warning} warning`} />
          <Chip size="small" color="info"    label={`ℹ️ ${counts.info} info`} />
        </Box>

        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 1 }}>
          <Tab label="Findings" />
          <Tab label="Summary text" />
          <Tab label="Raw JSON" />
        </Tabs>

        {tab === 0 && (
          <Box>
            {findings.length === 0 && (
              <Alert severity="success">No findings — this file passed every check.</Alert>
            )}
            {grouped.map(({ severity, items }) =>
              items.length === 0 ? null : (
                <Box key={severity} sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                    {SEVERITY_ICON[severity]} {severity} ({items.length})
                  </Typography>
                  {items.map((f, idx) => (
                    <Box
                      key={`${severity}-${idx}`}
                      sx={{
                        p: 1,
                        mb: 0.5,
                        borderLeft: 3,
                        borderColor: `${SEVERITY_COLOR[severity]}.main`,
                        backgroundColor: "#f8f9fa",
                        borderRadius: "0 4px 4px 0",
                      }}
                    >
                      <Typography variant="caption" sx={{ color: "text.secondary" }}>
                        <code>{f.check}</code> · subject: <code>{f.subject}</code>
                      </Typography>
                      <Typography variant="body2">{f.message}</Typography>
                      {f.data && (
                        <pre style={{ fontSize: "0.75rem", margin: "4px 0 0 0" }}>
                          {JSON.stringify(f.data, null, 2)}
                        </pre>
                      )}
                    </Box>
                  ))}
                </Box>
              )
            )}
          </Box>
        )}

        {tab === 1 && (
          <Box
            component="pre"
            sx={{
              fontSize: "0.78rem",
              backgroundColor: "#f8f9fa",
              p: 1.5,
              borderRadius: 1,
              overflowX: "auto",
              whiteSpace: "pre-wrap",
            }}
          >
            {summary}
          </Box>
        )}

        {tab === 2 && (
          <Box
            component="pre"
            sx={{
              fontSize: "0.72rem",
              backgroundColor: "#f8f9fa",
              p: 1.5,
              borderRadius: 1,
              overflowX: "auto",
            }}
          >
            {JSON.stringify(report, null, 2)}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
